import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Car, MapPin, Milestone, Info, Lightbulb, Shield, HelpCircle, X, Globe, Zap, AlertTriangle, TrendingDown, TrendingUp, Baby, Crosshair, Activity } from 'lucide-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import ImageViewerModal from '@shared/ui/Modals/ImageViewerModal';
import styles from './LessonContentRenderer.module.css';

interface SectionCardProps {
    number: string;
    title: string;
    summary: string;
    icon: string;
    image?: string;
    imageAlt?: string;
    imageFit?: 'cover' | 'contain';
    caption?: string;
    captionType?: 'bar' | 'icon';
    variant?: 'default' | 'theme' | 'authority' | 'impact' | 'stats';
    lottieUrl?: string;
}

const SectionCard: React.FC<SectionCardProps> = ({
    number,
    title,
    summary,
    icon,
    image,
    imageAlt,
    imageFit = 'cover',
    caption,
    captionType = 'bar',
    variant = 'default',
    lottieUrl
}) => {
    const [showCaption, setShowCaption] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [lottieError, setLottieError] = useState(false);

    const isValidLottie = Boolean(
        lottieUrl && 
        !lottieUrl.includes('placeholder') && 
        (lottieUrl.startsWith('http') || lottieUrl.startsWith('/') || lottieUrl.startsWith('assets/'))
    );
    const isThemedLottie = isValidLottie && !lottieError && (variant === 'theme' || variant === 'authority' || variant === 'impact' || variant === 'stats');

    const getIcon = () => {
        // Priority 1: Lottie Animation for themed variants
        if (isThemedLottie) {
            return (
                <div className={styles.sectionCardLottie}>
                    <DotLottieReact
                        src={lottieUrl}
                        autoplay
                        loop
                        className={styles.lottiePlayer}
                        onError={() => setLottieError(true)}
                    />
                </div>
            );
        }

        // Priority 2: Explicit suppression or missing icon
        if (!icon || icon.toLowerCase() === 'none') {
            return null;
        }

        // Priority 3: Image-based icon (PNG, SVG, etc)
        const isImagePath = icon.includes('/') || icon.toLowerCase().endsWith('.png') || icon.toLowerCase().endsWith('.svg') || icon.toLowerCase().endsWith('.webp');
        if (isImagePath) {
            let cleanIconPath = icon;
            if (cleanIconPath.startsWith('public/')) cleanIconPath = cleanIconPath.replace(/^public\//, '/');
            if (!cleanIconPath.startsWith('/') && !cleanIconPath.startsWith('http')) cleanIconPath = `/${cleanIconPath}`;

            return (
                <img
                    src={cleanIconPath}
                    alt=""
                    className={styles.sectionCardIconImage}
                    aria-hidden="true"
                />
            );
        }

        // Priority 4: Named Lucide Icon
        switch (icon.toLowerCase()) {
            case 'users': return <Users size={24} />;
            case 'car': return <Car size={24} />;
            case 'map-pin': return <MapPin size={24} />;
            case 'milestone': return <Milestone size={24} />;
            case 'info': return <Info size={24} />;
            case 'lightbulb': return <Lightbulb size={24} />;
            case 'shield': return <Shield size={24} />;
            case 'globe': return <Globe size={24} />;
            case 'zap': return <Zap size={24} />;
            case 'alert':
            case 'warning': return <AlertTriangle size={24} />;
            case 'trending-down': return <TrendingDown size={24} />;
            case 'trending-up': return <TrendingUp size={24} />;
            case 'baby': return <Baby size={24} />;
            case 'crosshair': return <Crosshair size={24} />;
            case 'activity': return <Activity size={24} />;
            default:
                // Only fallback to HelpCircle if it's the default variant or if we specially want to signal a missing icon
                // If there's an image, we'd rather show nothing than a generic question mark
                return image ? null : <HelpCircle size={24} />;
        }
    };

    // Format number to 01, 02 etc if it's a single digit
    const displayNumber = number.length === 1 ? `0${number}` : number;

    const imageClassName = `${styles.sectionCardImage} ${imageFit === 'contain' ? styles.sectionCardImageContain : ''}`;
    const containerClassName = `
        ${styles.sectionCardContainer} 
        ${variant === 'theme' ? styles.sectionCardTheme : ''} 
        ${variant === 'authority' ? styles.sectionCardAuthority : ''}
        ${variant === 'impact' ? styles.sectionCardImpact : ''}
        ${variant === 'stats' ? styles.sectionCardStats : ''}
    `.trim();

    return (
        <motion.div
            className={containerClassName}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5, ease: "easeOut" }}
        >
            <div className={styles.sectionCardAccent} />
            <div className={styles.sectionCardBody}>
                <div className={styles.sectionCardHeader}>
                    {variant === 'default' && (
                        <div className={styles.sectionCardNumber}>
                            {displayNumber}
                        </div>
                    )}
                    <div className={styles.sectionCardTitleContainer}>
                        <h3 className={styles.sectionCardTitle}>{title}</h3>
                        <div className={styles.sectionCardSummary} dangerouslySetInnerHTML={{ __html: summary }} />
                    </div>
                    {getIcon() && (
                        <div className={`${styles.sectionCardIcon} ${variant !== 'default' ? styles.sectionCardIconTheme : ''} ${isThemedLottie ? styles.sectionCardIconLottieTheme : ''}`}>
                            {getIcon()}
                        </div>
                    )}
                </div>

                {image && (
                    <div className={styles.sectionCardImageContainer}>
                        <img
                            src={image}
                            alt={imageAlt || title}
                            className={imageClassName}
                            onClick={() => setShowImageModal(true)}
                            style={{ cursor: 'pointer' }}
                            role="button"
                            tabIndex={0}
                            aria-label={`View ${imageAlt || title} in full screen`}
                        />

                        {caption && (
                            <>
                                {captionType === 'bar' ? (
                                    <div className={styles.imageCaptionBar}>
                                        <Info size={14} className={styles.captionInfoIcon} />
                                        <span>{caption}</span>
                                    </div>
                                ) : (
                                    <div className={styles.imageCaptionIconContainer}>
                                        <button
                                            className={`${styles.captionIconButton} ${showCaption ? styles.active : ''}`}
                                            onClick={() => setShowCaption(!showCaption)}
                                            onMouseEnter={() => setShowCaption(true)}
                                            onMouseLeave={() => setShowCaption(false)}
                                            aria-label="View description"
                                        >
                                            <Info size={18} />
                                        </button>
                                        <AnimatePresence>
                                            {showCaption && (
                                                <motion.div
                                                    className={styles.captionExpandedCard}
                                                    initial={{ opacity: 0, scale: 0.9, y: 10 }}
                                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                                    exit={{ opacity: 0, scale: 0.9, y: 10 }}
                                                >
                                                    <p>{caption}</p>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            <ImageViewerModal
                isOpen={showImageModal}
                imageUrl={image || ''}
                imageAlt={imageAlt || title}
                caption={caption}
                onClose={() => setShowImageModal(false)}
            />
        </motion.div>
    );
};

export default SectionCard;
